(function () {
  const DRAWER_ID = 'cart-drawer';
  const SECTION_ID = 'cart-drawer';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Whatever had focus before the drawer opened — focus goes back there on close.
  let lastFocused = null;
  // Elements this script made inert, so closing only undoes its own changes.
  let inerted = [];

  function getDrawer() {
    return document.getElementById(DRAWER_ID);
  }

  function isOpen() {
    const drawer = getDrawer();
    return !!drawer && drawer.classList.contains('cart-drawer--open');
  }

  function getFocusable(drawer) {
    const panel = drawer.querySelector('.cart-drawer__panel') || drawer;
    return Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);
  }

  function focusCloseButton(drawer) {
    const target = drawer.querySelector('button[data-cart-drawer-close]') || getFocusable(drawer)[0];
    if (target) target.focus();
  }

  // Everything outside the drawer (skip link, header, main, footer, app embeds)
  // goes inert while it's open, so neither Tab nor a screen reader can reach
  // the page behind it.
  function setPageInert(on) {
    if (on) {
      const drawer = getDrawer();
      inerted = Array.from(document.body.children).filter(
        (el) => !el.contains(drawer) && !el.inert && el.tagName !== 'SCRIPT'
      );
      inerted.forEach((el) => {
        el.inert = true;
      });
    } else {
      inerted.forEach((el) => {
        el.inert = false;
      });
      inerted = [];
    }
  }

  function openDrawer() {
    const drawer = getDrawer();
    if (!drawer) return;
    if (!isOpen()) {
      lastFocused = document.activeElement;
      setPageInert(true);
    }
    drawer.classList.add('cart-drawer--open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    focusCloseButton(drawer);
  }

  function closeDrawer() {
    const drawer = getDrawer();
    if (!drawer || !isOpen()) return;
    drawer.classList.remove('cart-drawer--open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    setPageInert(false);
    if (lastFocused && document.contains(lastFocused)) lastFocused.focus();
    lastFocused = null;
  }

  // Selector for a qty/remove button, so focus can be put back on its
  // replacement after the drawer HTML is swapped out.
  function focusSelector(el) {
    const key = el.getAttribute('data-cart-change');
    if (!key) return '';
    let selector = `[data-cart-change="${CSS.escape(key)}"]`;
    const delta = el.getAttribute('data-cart-delta');
    const qty = el.getAttribute('data-cart-qty');
    if (delta !== null) selector += `[data-cart-delta="${delta}"]`;
    if (qty !== null) selector += `[data-cart-qty="${qty}"]`;
    return selector;
  }

  function setLoading(loading) {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.toggle('cart-drawer--loading', !!loading);
  }

  async function refreshDrawer() {
    setLoading(true);
    try {
      const res = await fetch('/?sections=' + SECTION_ID, { credentials: 'same-origin' });
      const data = await res.json();
      const html = data[SECTION_ID];
      if (!html) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const fresh = doc.getElementById(DRAWER_ID);
      const existing = getDrawer();
      if (fresh && existing) {
        const wasOpen = existing.classList.contains('cart-drawer--open');
        const active = document.activeElement;
        const refocus = existing.contains(active) ? focusSelector(active) : null;
        existing.innerHTML = fresh.innerHTML;
        existing.setAttribute('data-item-count', fresh.getAttribute('data-item-count') || '0');
        if (wasOpen) existing.classList.add('cart-drawer--open');
        // The swap destroys the focused button; land on its replacement, or
        // the close button if the line item is gone.
        if (wasOpen && refocus !== null) {
          const target = refocus && existing.querySelector(refocus);
          if (target) target.focus();
          else focusCloseButton(existing);
        }
      }

      // Update header cart count (text label + mobile badge)
      const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await cartRes.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
      });
    } finally {
      setLoading(false);
    }
  }

  async function changeItem(key, quantity) {
    setLoading(true);
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity: quantity }),
      });
      await refreshDrawer();
    } catch (err) {
      console.error('Cart change failed', err);
      setLoading(false);
    }
  }

  async function addToCart(formData) {
    setLoading(true);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart');
      }
      await refreshDrawer();
      openDrawer();
    } catch (err) {
      console.error(err);
      alert(err.message || 'Could not add to cart');
    } finally {
      setLoading(false);
    }
  }

  // Delegated change handler — trade-confirmation checkbox gates checkout + persists as cart attribute
  document.addEventListener('change', (e) => {
    const box = e.target.closest('[data-trade-confirm]');
    if (!box) return;
    const drawer = getDrawer();
    const gateBtn = drawer ? drawer.querySelector('[data-trade-gate]') : null;
    if (gateBtn) gateBtn.disabled = !box.checked;
    // Also gate any twin button on the cart page
    document.querySelectorAll('[data-trade-gate]').forEach((btn) => {
      btn.disabled = !box.checked;
    });
    // Persist to cart attributes so the choice survives navigation
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { trade_confirmed: box.checked ? 'yes' : '' } }),
    }).catch(() => {});
  });

  // Delegated click handler — drawer toggles, qty changes, removes
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-drawer-open]');
    if (opener) {
      e.preventDefault();
      openDrawer();
      return;
    }

    const closer = e.target.closest('[data-cart-drawer-close]');
    if (closer) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    const changer = e.target.closest('[data-cart-change]');
    if (changer) {
      e.preventDefault();
      const key = changer.getAttribute('data-cart-change');
      const explicitQty = changer.getAttribute('data-cart-qty');
      if (explicitQty !== null) {
        changeItem(key, parseInt(explicitQty, 10));
        return;
      }
      const delta = parseInt(changer.getAttribute('data-cart-delta'), 10) || 0;
      const valueEl = changer
        .closest('.cart-drawer__qty')
        .querySelector('.cart-drawer__qty-value');
      const current = parseInt((valueEl && valueEl.textContent) || '0', 10);
      changeItem(key, Math.max(0, current + delta));
    }
  });

  // While open: Esc closes, Tab / Shift+Tab wrap around inside the drawer
  document.addEventListener('keydown', (e) => {
    if (!isOpen()) return;
    if (e.key === 'Escape') {
      closeDrawer();
      return;
    }
    if (e.key !== 'Tab') return;

    const drawer = getDrawer();
    const items = getFocusable(drawer);
    if (!items.length) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const inside = drawer.contains(active);
    if (e.shiftKey && (active === first || !inside)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !inside)) {
      e.preventDefault();
      first.focus();
    }
  });

  // Intercept product form submits — add via Ajax + open drawer
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.getAttribute('action') && form.getAttribute('action').indexOf('/cart/add') === -1) {
      // Only intercept product add-to-cart forms
      const action = form.getAttribute('action') || '';
      if (action.indexOf('/cart') === -1) return;
    }
    // Shopify product forms have name="add" or are action="/cart/add"
    const isProductForm =
      form.matches('form[action*="/cart/add"]') ||
      form.matches('form.product-form') ||
      form.querySelector('input[name="id"]');
    if (!isProductForm) return;

    // Don't intercept the cart page's checkout form or other cart actions
    if (form.matches('form[action$="/cart"]')) return;

    e.preventDefault();
    const fd = new FormData(form);
    addToCart(fd);
  });
})();
